import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contracts } from "@/api/endpoints";
import { Money } from "@/components/shared/Money";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import { useAuthStore } from "@/store/authStore";
import type { CreditReason } from "@/types/api";

const REASON_LABELS: Record<CreditReason, string> = {
  outage: "Outage",
  late_delivery: "Late delivery",
  sla_breach: "SLA breach",
  goodwill: "Goodwill",
  other: "Other",
};

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY = {
  credit_date: TODAY,
  spread: false,
  end_date: TODAY,
  amount: "",
  reason: "outage" as CreditReason,
  notes: "",
};

function daysInclusive(start: string, end: string): number {
  return Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000) + 1;
}

const fmtDaily = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

// Credits reduce recognized contract revenue on their credit date, or evenly
// per day over a period (split across the contract's servers), so every
// revenue view nets them out.
export function ContractCredits({ contractId }: { contractId: string }) {
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === "editor" || role === "admin";
  const [form, setForm] = useState(EMPTY);

  const q = useQuery({
    queryKey: ["contracts", contractId, "credits"],
    queryFn: () => contracts.listCredits(contractId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["contracts", contractId, "credits"] });
    qc.invalidateQueries({ queryKey: ["revenue"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
  };

  const add = useMutation({
    mutationFn: () =>
      contracts.addCredit(contractId, {
        credit_date: form.credit_date,
        end_date: form.spread ? form.end_date : null,
        amount: form.amount,
        reason: form.reason,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      invalidate();
      setForm(EMPTY);
      showToast("Credit added.", "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "add credit failed", "error"),
  });

  const remove = useMutation({
    mutationFn: (creditId: string) => contracts.deleteCredit(contractId, creditId),
    onSuccess: () => {
      invalidate();
      showToast("Credit removed.", "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "remove credit failed", "error"),
  });

  const credits = q.data ?? [];
  const spreadDays = form.spread ? daysInclusive(form.credit_date, form.end_date) : 1;
  const spreadInvalid = form.spread && !(spreadDays >= 1);
  const total = credits.reduce((s, c) => s + Number(c.amount), 0);

  return (
    <section className="space-y-3 max-w-3xl">
      <div className="flex items-baseline justify-between">
        <h2 className="text-base font-semibold">Credits</h2>
        {credits.length > 0 && (
          <span className="text-sm text-slate-500">
            Total credited: <Money value={-total} />
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500">
        Credits reduce this contract&apos;s recognized revenue on the credit date, or evenly per
        day when spread over a period.
      </p>

      {canEdit && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!spreadInvalid) add.mutate();
          }}
          className="space-y-2 text-sm"
        >
          <div className="grid grid-cols-[9rem_9rem_8rem_10rem_1fr_auto] items-end gap-2">
            <label className="block">
              <span className="text-slate-600 dark:text-slate-300">
                {form.spread ? "Start date" : "Date"}
              </span>
              <input
                type="date"
                value={form.credit_date}
                onChange={(e) => setForm({ ...form, credit_date: e.target.value })}
                required
                className="input mt-1"
              />
            </label>
            <label className="block">
              <span className="text-slate-600 dark:text-slate-300">End date</span>
              <input
                type="date"
                value={form.spread ? form.end_date : ""}
                min={form.credit_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                required={form.spread}
                disabled={!form.spread}
                className="input mt-1 disabled:opacity-40"
              />
            </label>
            <label className="block">
              <span className="text-slate-600 dark:text-slate-300">Amount (USD)</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
                className="input mt-1"
              />
            </label>
            <label className="block">
              <span className="text-slate-600 dark:text-slate-300">Reason</span>
              <select
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value as CreditReason })}
                className="input mt-1"
              >
                {Object.entries(REASON_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-slate-600 dark:text-slate-300">Notes</span>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. 14h outage on node 3, ticket #123"
                className="input mt-1"
              />
            </label>
            <button
              type="submit"
              disabled={add.isPending}
              className="rounded-md bg-brand-500 px-3 py-2 text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Add credit
            </button>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <label className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.spread}
                onChange={(e) =>
                  setForm({
                    ...form,
                    spread: e.target.checked,
                    end_date: form.end_date < form.credit_date ? form.credit_date : form.end_date,
                  })
                }
              />
              Spread over a period
            </label>
            {form.spread &&
              (spreadInvalid ? (
                <span className="text-red-600">End date must be on or after the start date.</span>
              ) : (
                Number(form.amount) > 0 && (
                  <span className="text-slate-500">
                    {fmtDaily.format(Number(form.amount) / spreadDays)}/day over {spreadDays} day
                    {spreadDays === 1 ? "" : "s"}
                  </span>
                )
              ))}
          </div>
        </form>
      )}

      {q.isLoading ? (
        <Spinner />
      ) : q.error ? (
        <div className="text-red-700">{(q.error as Error).message}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 text-left">Period</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Amount</th>
                {canEdit && <th className="px-3 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {credits.length === 0 && (
                <tr>
                  <td colSpan={canEdit ? 5 : 4} className="px-3 py-6 text-center text-slate-400">
                    No credits issued.
                  </td>
                </tr>
              )}
              {credits.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {c.end_date && c.end_date !== c.credit_date ? (
                      <>
                        {c.credit_date} → {c.end_date}
                        <span className="ml-1 text-xs text-slate-400">
                          ({daysInclusive(c.credit_date, c.end_date)}d)
                        </span>
                      </>
                    ) : (
                      c.credit_date
                    )}
                  </td>
                  <td className="px-3 py-2">{REASON_LABELS[c.reason]}</td>
                  <td className="px-3 py-2 text-slate-500">{c.notes ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-red-700 dark:text-red-400">
                    <Money value={-Number(c.amount)} />
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm("Remove this credit? Revenue will be restored.")) remove.mutate(c.id);
                        }}
                        disabled={remove.isPending}
                        className="text-xs text-red-600 hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
